import { useContext, useEffect, useMemo, useState } from "react";
import AdvancedFilter from "../executive-search/AdvancedFilter";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";
import { Info, Filter, CheckCircle2 } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  getCompanyTypeList,
  getEmployeeCountList,
  getExecutiveEmployeeList,
  getExecutiveFunctionList,
  getExecutiveLevelList,
  getIndustryList,
  getLocation,
  getRevenuerangeList,
  saveExecutiveSearchList,
} from "../../actionCreator/executiveListingActionCreater";
import { getTagList } from "../../actionCreator/dashboardActionCreator";
import { getPartialPhoneNumber, getUserInfo } from "../../utils/utils";
import { AuthContext } from "../../context/AuthContext";
import Loader from "../loader";
import { Button, Table, Tooltip, Checkbox } from "antd";
import { colorArray } from "../../constant/profileBgcolors";
import {
  getSummary,
  importEmployees,
} from "../../actionCreator/importActionCreator";
import { useAlert } from "../../context/AlertProvider";
import addedToLead from "../../assets/svg/added-to-lead.svg";
import viewEmail from "../../assets/svg/view-email.svg";
import CopyText from "../web2lead/CopyText";
import { getEmployeeViewableStatusUpdate } from "../../actionCreator/executiveDetailsActionCreator";
import viewEmailChecked from "../../assets/svg/view-email-checked.svg";
import copy from "../../assets/images/webToLead/copy.png";
import CustomPagination from "../../common/CustomPagination";

const BeyondleadzData = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { isAuthenticated } = useContext(AuthContext);
  const { id } = getUserInfo();
  const [showEmail, setShowEmail] = useState({});
  const loading = useSelector((state) => state.CommonReducer.loading);
  const executiveList = useSelector((state) => state.executiveListingReducer);
  const isLoading = useSelector(
    (state) => state.executiveListingReducer.loading,
  );
  const hasOtherFilters = Array.from(searchParams.keys()).some(
    (key) => key !== "page" && key !== "size" && key !== "status",
  );
  const getEmployeeViewableData = useSelector(
    (state) => state.executiveDetailsReducer?.executiveEmployeeViewableStatus,
  );
  const [search, setSearch] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isSelectAllSearched, setIsSelectAllSearched] = useState(false);

  const currentDataSource = useMemo(() => {
    return (
      executiveList?.executiveEmployeeList?.map((d, idx) => ({
        ...d,
        key: d?.id || idx,
        companyName: d?.company?.name,
      })) || []
    );
  }, [executiveList]);

  const totalSearchedCount =
    executiveList?.totalExecutiveCount || currentDataSource.length;

  useEffect(() => {
    if (currentDataSource?.length) {
      setSelectedRowKeys(currentDataSource.map((item) => item.key));
    } else {
      setSelectedRowKeys([]);
      setIsSelectAllSearched(false);
    }
  }, [currentDataSource]);

  const handleSelectAllPage = (e) => {
    if (e.target.checked) {
      setSelectedRowKeys(currentDataSource.map((item) => item.key));
    } else {
      setSelectedRowKeys([]);
      setIsSelectAllSearched(false);
    }
  };

  const handleSelectAllSearched = () => {
    setIsSelectAllSearched(true);
    setSelectedRowKeys(currentDataSource.map((item) => item.key));
  };

  const handleClearSelection = () => {
    setIsSelectAllSearched(false);
    setSelectedRowKeys([]);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
      if (newSelectedRowKeys.length < currentDataSource.length) {
        setIsSelectAllSearched(false);
      }
    },
  };

  useEffect(() => {
    dispatch(getIndustryList());
    dispatch(getLocation());
    dispatch(getCompanyTypeList());
    dispatch(getEmployeeCountList());
    dispatch(getRevenuerangeList());
    dispatch(getExecutiveFunctionList());
    dispatch(getExecutiveLevelList());
    if (isAuthenticated) {
      dispatch(saveExecutiveSearchList(id));
      dispatch(getTagList("EXECUTIVE"));
    }
  }, [dispatch, isAuthenticated, id]);

  const updateEmailStatus = async (showEmail, row) => {
    setShowEmail({ ...showEmail, [row.key]: true });
    const res = await dispatch(getEmployeeViewableStatusUpdate("Email", row));
    if (res?.status === 400) {
      showAlert("error", res?.title);
    } else {
      const allParams = {};
      for (const [key, value] of searchParams.entries()) {
        allParams[key] = value;
      }
      dispatch(
        getExecutiveEmployeeList({
          ...allParams,
          sort: "fullname,asc",
          page: allParams?.page ? allParams?.page - 1 : 0,
        }),
      );
    }
  };

  const onSearch = () => {
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }

    dispatch(
      getExecutiveEmployeeList({
        ...allParams,
        sort: "fullname,asc",
        page: allParams?.page ? allParams?.page - 1 : 0,
      }),
    );
    setSearch(true);
    setIsSelectAllSearched(false);
  };

  const isFullImport =
    isSelectAllSearched ||
    (currentDataSource.length > 0 &&
      selectedRowKeys.length === currentDataSource.length);

  const onImport = async () => {
    if (!selectedRowKeys?.length) {
      showAlert("warning", "Please select at least one record to import.");
      return;
    }
    const allParams = {};
    for (const [key, value] of searchParams.entries()) {
      allParams[key] = value;
    }

    // When Select All is active, import all search results matching query params as previous
    const payload = isFullImport
      ? {
          ...allParams,
          sort: "fullname,asc",
          page: undefined,
        }
      : {
          ...allParams,
          "id.in": selectedRowKeys.join(","),
          sort: "fullname,asc",
          page: undefined,
        };

    const importCount = isFullImport
      ? totalSearchedCount
      : selectedRowKeys.length;

    const res = await dispatch(importEmployees(payload));

    if (res?.status === 200) {
      showAlert(
        "success",
        `${importCount} lead(s) imported successfully!`,
      );
      await dispatch(getSummary());
      navigate(location.pathname);
    } else {
      showAlert("error", res?.response?.data?.title || "Failed to import leads");
    }
  };

  const columns = [
    {
      title: "Executive Name",
      dataIndex: "name",
      fixed: "left",
      width: 240,
      render: (record, row, index) => {
        const name = row?.fullname
          ? row?.fullname
          : row?.firstname && row?.lastname
            ? `${row.firstname} ${row.lastname}`
            : row?.firstname || "Executive";
        const initialFirst = row?.firstname?.[0] || name?.[0] || "E";
        const initialLast = row?.lastname?.[0] || "";

        return (
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-size-[36px] tw-min-w-[36px] tw-border-slate-200/90 tw-border-solid tw-uppercase tw-font-bold tw-text-xs tw-bg-indigo-50 tw-text-theme">
              {initialFirst}
              {initialLast}
            </div>
            <div className="tw-flex tw-flex-col tw-min-w-0">
              <Tooltip title={name}>
                <span className="tw-font-bold tw-text-slate-800 tw-text-xs tw-truncate tw-block">
                  {name}
                </span>
              </Tooltip>
              {row?.isLead && (
                <Tooltip title="Added to lead">
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold tw-text-emerald-600 tw-bg-emerald-50 tw-px-1.5 tw-py-0.5 tw-rounded-md tw-w-fit tw-mt-0.5">
                    <img src={addedToLead} alt="" className="tw-w-3 tw-h-3" /> Added to lead
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "companyName",
      render: (record, row) => {
        const companyName = row?.company?.name || record || "-";
        return (
          <Tooltip title={companyName}>
            <span className="tw-text-xs tw-font-semibold tw-text-slate-700 tw-truncate tw-block">
              {companyName}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Designation",
      dataIndex: "title",
      render: (record) => {
        const title = record || "-";
        return (
          <Tooltip title={title}>
            <span className="tw-text-xs tw-font-medium tw-text-slate-600 tw-truncate tw-block">
              {title}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      className: "email",
      width: 180,
      render: (text, row) => {
        return (
          <div
            className={`d-flex align-items-center ${row.emailId === getEmployeeViewableData?.emailId && "justify-content-between"}`}
            style={{ gap: 10 }}
          >
            {row.emailId === getEmployeeViewableData?.emailId ? (
              <div className="d-flex align-items-center gap-10">
                <Tooltip title={getEmployeeViewableData?.emailId}>
                  <span className="tw-text-xs tw-font-semibold tw-text-slate-700">
                    {getEmployeeViewableData?.emailId?.length > 15
                      ? `${getEmployeeViewableData?.emailId.substring(0, 12)}...`
                      : getEmployeeViewableData?.emailId}
                  </span>
                </Tooltip>
                <div className="email-grp-btn" style={{ padding: "0 3px" }}>
                  <CopyText
                    text={getEmployeeViewableData?.emailId}
                    content={
                      <Tooltip title="Copy Email">
                        <img
                          src={copy}
                          alt=""
                          style={{
                            height: "23px",
                            width: "23px",
                            padding: "4px",
                            maxWidth: "23px",
                          }}
                          className="cursor-pointer"
                        />
                      </Tooltip>
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="tw-flex tw-items-center tw-gap-2">
                <span
                  className="tw-text-xs tw-font-bold tw-text-theme hover:tw-underline tw-cursor-pointer"
                  onClick={() => updateEmailStatus(showEmail, row)}
                >
                  View Email
                </span>
                <img
                  src={row?.isdownloadedEmail ? viewEmailChecked : viewEmail}
                  alt=""
                  className="tw-cursor-pointer tw-h-4 tw-w-4"
                  onClick={() => updateEmailStatus(showEmail, row)}
                />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Phone",
      dataIndex: "phoneNo",
      className: "phone",
      render: (text) => {
        const number = getPartialPhoneNumber(text) || "-";
        return (
          <Tooltip title={number}>
            <span className="tw-text-xs tw-font-medium tw-text-slate-600 tw-truncate tw-block">
              {number}
            </span>
          </Tooltip>
        );
      },
    },
  ];

  const hasMandatorySelection =
    !!searchParams?.get("Countries") &&
    !!searchParams?.get("States") &&
    !!searchParams?.get("Cities") &&
    !!searchParams.get("Industry");

  return (
    <div className="import-beyondleadz mt30">
      <div className="d-flex align-items-center mb10 justify-content-between">
        {search && (
          <h2 className="roboto-bold heading20px">
            {search ? "Search Results" : ""}
          </h2>
        )}
        {search && (
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <Button
              onClick={() => setSearch(false)}
              className="!tw-h-[38px] !tw-px-4 !tw-rounded-xl !tw-bg-indigo-50/80 hover:!tw-bg-indigo-100/90 !tw-border !tw-border-solid !tw-border-indigo-200/80 tw-text-theme tw-font-bold tw-text-xs tw-flex tw-items-center tw-gap-2 tw-transition-all hover:tw-scale-[1.02] tw-cursor-pointer"
            >
              <Filter size={15} className="tw-text-theme" />
              <span>Change Filters</span>
            </Button>
            {hasOtherFilters && (
              <Button
                onClick={() => {
                  navigate(`${location.pathname}?status=beyondleadzData`, {
                    replace: true,
                  });
                  setSearch(false);
                }}
                className="!tw-h-[38px] !tw-px-4 !tw-rounded-xl !tw-bg-slate-50 hover:!tw-bg-red-50 !tw-border !tw-border-solid !tw-border-slate-200/80 hover:!tw-border-red-200 tw-text-slate-600 hover:tw-text-red-600 tw-font-bold tw-text-xs tw-flex tw-items-center tw-gap-2 tw-transition-all hover:tw-scale-[1.01] tw-cursor-pointer"
              >
                <span>Clear Filters</span>
                <CloseOutlined className="tw-text-xs" />
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="leftmenuheading">
        <div className="advancedfilter">
          <div className="leftmenufilter">
            {!loading ? (
              <>
                {search ? (
                  <>
                    <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-justify-between tw-bg-indigo-50/60 tw-px-4 tw-py-3 tw-rounded-xl tw-mb-3 tw-border tw-border-solid tw-border-indigo-100 tw-gap-2">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <Checkbox
                          checked={
                            isSelectAllSearched ||
                            (currentDataSource.length > 0 &&
                              selectedRowKeys.length === currentDataSource.length)
                          }
                          indeterminate={
                            !isSelectAllSearched &&
                            selectedRowKeys.length > 0 &&
                            selectedRowKeys.length < currentDataSource.length
                          }
                          onChange={handleSelectAllPage}
                          className="tw-font-bold tw-text-xs tw-text-slate-700"
                        >
                          Select Page ({currentDataSource.length})
                        </Checkbox>

                        {isSelectAllSearched ? (
                          <span className="tw-text-xs tw-font-bold tw-text-theme tw-flex tw-items-center tw-gap-1.5">
                            <CheckCircle2 size={14} className="tw-text-theme" />
                            All {totalSearchedCount} searched items selected
                          </span>
                        ) : (
                          <span className="tw-text-xs tw-font-semibold tw-text-slate-600">
                            {selectedRowKeys.length} of {currentDataSource.length} on page selected
                          </span>
                        )}
                      </div>

                      <div className="tw-flex tw-items-center tw-gap-2">
                        {isSelectAllSearched ? (
                          <button
                            type="button"
                            onClick={handleClearSelection}
                            className="tw-text-xs tw-font-bold tw-text-slate-500 hover:tw-text-red-600 tw-underline tw-cursor-pointer tw-bg-transparent tw-border-none"
                          >
                            Clear Selection
                          </button>
                        ) : totalSearchedCount > currentDataSource.length ? (
                          <button
                            type="button"
                            onClick={handleSelectAllSearched}
                            className="tw-text-xs tw-font-bold tw-text-theme hover:tw-text-[#4B35E0] tw-underline tw-cursor-pointer tw-bg-transparent tw-border-none"
                          >
                            Select all {totalSearchedCount} searched items
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="tw-custom-table-container">
                      <Table
                        rowSelection={rowSelection}
                        columns={columns?.map((col) => ({
                          ...col,
                          onCell: () => ({
                            className: "word-break-keep-all",
                          }),
                        }))}
                        className="tw-custom-table tw-executive-search-table"
                        scroll={{ x: "max-content" }}
                        dataSource={currentDataSource}
                        pagination={false}
                      />
                      <CustomPagination
                        total={executiveList?.totalExecutiveCount || 0}
                        current={searchParams.get("page") || 1}
                        pageSize={10}
                        onChange={(page) => {
                          searchParams.set("page", page);
                          navigate(
                            `${location.pathname}?${searchParams.toString()}`,
                          );
                        }}
                        noBorder={true}
                      />
                    </div>
                    <div className="d-flex justify-content-between mt30">
                      <div className="d-flex align-items-center gap-10">
                        <Button
                          className="light-btn d-flex align-items-center gap-10"
                          disabled={isLoading}
                          onClick={() => {
                            navigate(location.pathname);
                          }}
                        >
                          <ArrowLeftOutlined className="fw-bold" /> Back to
                          sources
                        </Button>

                        <Button
                          disabled={isLoading}
                          onClick={() => setSearch(false)}
                          className="!tw-h-[38px] !tw-px-4 !tw-rounded-xl !tw-bg-indigo-50/80 hover:!tw-bg-indigo-100/90 !tw-border !tw-border-solid !tw-border-indigo-200/80 tw-text-theme tw-font-bold tw-text-xs tw-flex tw-items-center tw-gap-2 tw-transition-all hover:tw-scale-[1.02] tw-cursor-pointer"
                        >
                          <Filter size={15} className="tw-text-theme" />
                          <span>Change Filters</span>
                        </Button>
                      </div>

                      {!!executiveList?.executiveEmployeeList?.length && (
                        <Button
                          className="theme-btn"
                          type="primary"
                          loading={isLoading}
                          disabled={!selectedRowKeys.length || isLoading}
                          onClick={onImport}
                        >
                          Import{" "}
                          {isFullImport
                            ? `All (${totalSearchedCount})`
                            : `Selected (${selectedRowKeys.length})`}
                        </Button>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <AdvancedFilter
                      isModal
                      isImport
                      openAdvancedModel
                      showNumberofRecords={36}
                      isShow
                    />
                    {!hasMandatorySelection && (
                      <div className="tw-mt-4 tw-p-3.5 tw-rounded-xl tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200/80 tw-flex tw-items-center tw-justify-between tw-text-amber-800 tw-text-xs tw-font-semibold">
                        <div className="tw-flex tw-items-center tw-gap-2">
                          <Info size={16} className="tw-text-amber-600 tw-flex-shrink-0" />
                          <span>
                            <strong className="tw-font-bold">Mandatory Search Filters:</strong>{" "}
                            {!searchParams?.get("Countries") && "Country, "}{" "}
                            {!searchParams?.get("States") && "State, "}{" "}
                            {!searchParams?.get("Cities") && "City, "}{" "}
                            {!searchParams.get("Industry") && "Industry "} selection
                            {!searchParams?.get("Countries") || !searchParams?.get("States") || !searchParams?.get("Cities") ? "s are" : " is"} required to search.
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="d-flex justify-content-between mt10">
                      <Button
                        className="light-btn d-flex align-items-center gap-10"
                        disabled={loading}
                        onClick={() => {
                          navigate(location.pathname);
                        }}
                      >
                        <ArrowLeftOutlined className="fw-bold" /> Back to
                        sources
                      </Button>

                      <Button
                        className="theme-btn"
                        type="primary"
                        disabled={!hasMandatorySelection}
                        onClick={onSearch}
                      >
                        Search
                      </Button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <Loader />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeyondleadzData;
